/**
 * Writes the `option_hints` of a meter's spec metaobject as a second line into
 * the variant cards of the Horizon variant picker.
 *
 * The picker owns its own markup and re-renders it on every variant change, so
 * the hints are re-applied from a MutationObserver rather than injected once.
 * Applying is idempotent: an unchanged hint writes nothing, which is what keeps
 * the observer from feeding itself.
 *
 * Plain custom element rather than the theme `Component` base — there is no
 * shadow DOM, no refs and no server round trip involved.
 */
class MeterOptionHints extends HTMLElement {
  connectedCallback() {
    this.hints = this.#readHints();
    if (this.hints.size === 0) return;

    // The variant picker lives in the same details column as this element.
    this.root = this.closest('.product-details') ?? this.getRootNode();

    this.#apply();

    this.observer = new MutationObserver(() => this.#schedule());
    this.observer.observe(this.root, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  #readHints() {
    const map = new Map();
    const payload = this.querySelector('script[type="application/json"]');
    if (!payload) return map;

    let pairs;
    try {
      pairs = JSON.parse(payload.textContent);
    } catch {
      // Malformed data must not take the buy box down with it.
      return map;
    }

    if (!Array.isArray(pairs)) return map;
    for (const pair of pairs) {
      if (!Array.isArray(pair) || pair.length < 2) continue;
      map.set(MeterOptionHints.normalize(pair[0]), String(pair[1]));
    }
    return map;
  }

  /**
   * Compare keys forgivingly: inch marks reach Shopify as ", ″ or ” depending on
   * the keyboard, and editors are not going to match the merchandiser's spacing.
   */
  static normalize(value) {
    return String(value)
      .replace(/[“”„″ʺ]/g, '"')
      .replace(/[‘’′]/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  #schedule() {
    if (this.applying) return;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#apply());
  }

  #apply() {
    this.frame = null;
    this.applying = true;

    for (const label of this.root.querySelectorAll('.variant-option__button-label')) {
      const input = label.querySelector('input[value]');
      const text = label.querySelector('.variant-option__button-label__text');
      if (!input || !text) continue;

      const hint = this.hints.get(MeterOptionHints.normalize(input.value));
      let line = text.querySelector('.meter-opt-hint');

      if (!hint) {
        line?.remove();
        continue;
      }

      if (!line) {
        line = document.createElement('span');
        line.className = 'meter-opt-hint';
        text.appendChild(line);
      }

      if (line.textContent !== hint) line.textContent = hint;
    }

    this.applying = false;
  }
}

if (!customElements.get('meter-option-hints')) {
  customElements.define('meter-option-hints', MeterOptionHints);
}
